import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { useCanvasStore } from '@/stores/canvasStore';
import { MindBubbleItem } from './MindBubbleItem';

interface MindBubbleProps {
  onClose: () => void;
  onDragItem: (item: any, position: { x: number; y: number }) => void;
}

export function MindBubble({ onClose, onDragItem }: MindBubbleProps) {
  const { allItems } = useCanvasStore();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Get pinned items only
  const pinnedItems = allItems.filter(item => item.is_pinned_wall);
  
  // Don't render if no pinned items
  if (pinnedItems.length === 0) return null;
  
  return (
    <div
      className="fixed top-20 right-6 z-50"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Bubble container */}
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          /* Collapsed state - just icon */
          <motion.div
            key="collapsed"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative"
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/80 shadow-lg flex items-center justify-center cursor-pointer"
            >
              <Sparkles className="w-6 h-6 text-primary-foreground" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full text-xs font-bold text-white flex items-center justify-center shadow">
                {pinnedItems.length}
              </span>
            </motion.div>
            
            {/* Pulse animation ring */}
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-primary"
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        ) : (
          /* Expanded state */
          <motion.div
            key="expanded"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
            style={{ width: 280 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Pinned Items</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            {/* Scrollable item grid */}
            <div className="p-3 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2">
                {pinnedItems.map(item => (
                  <MindBubbleItem
                    key={item.id}
                    item={item}
                    onDragEnd={(position) => onDragItem(item, position)}
                  />
                ))}
              </div>
            </div>
            
            {/* Footer hint */}
            <div className="px-4 py-2 bg-muted/30 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                Drag items onto the canvas
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
